import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Trash2, MessageSquarePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Timestamped notes for one virtual-audition video (acting/vocal/dance).
 *
 * Judges type a time in ``m:ss`` (or ``h:mm:ss``) + a short note. Clicking
 * an existing timestamp calls ``onSeek(sec)`` so the parent can rebuild the
 * iframe src with a ``?start=`` query — a lightweight "seek" that works with
 * the plain YouTube / Vimeo embed URLs we already use (no player API JS).
 */
function parseTs(s) {
  const parts = s.trim().split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n) || n < 0)) return null;
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

function fmtTs(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h
    ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    : `${m}:${String(ss).padStart(2, "0")}`;
}

export default function VideoNotes({ auditionId, videoKey, onSeek }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [tsInput, setTsInput] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/auditions/${auditionId}/video-notes`);
      setNotes((data || []).filter((n) => n.video_key === videoKey));
    } catch { /* silent — judges without permission see empty */ }
  };
  useEffect(() => { if (auditionId) load(); }, [auditionId, videoKey]);

  const add = async () => {
    if (!text.trim()) return toast.error("Add a note");
    const sec = parseTs(tsInput || "0:00");
    if (sec === null) return toast.error("Time must look like m:ss or h:mm:ss");
    setSaving(true);
    try {
      await api.post(`/auditions/${auditionId}/video-notes`, {
        video_key: videoKey, timestamp_sec: sec, note: text.trim(),
      });
      setText("");
      setTsInput("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    try {
      await api.delete(`/audition-video-notes/${id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-2 space-y-1.5" data-testid={`video-notes-${videoKey}`}>
      {notes.length > 0 && (
        <ul className="space-y-1">
          {notes.map((n) => (
            <li key={n.id} className="rounded border border-white/10 bg-[#0A0A0A] p-2 text-xs" data-testid={`video-note-${n.id}`}>
              <div className="flex items-start gap-2">
                <button
                  onClick={() => onSeek?.(n.timestamp_sec)}
                  className="shrink-0 font-mono text-gold hover:underline inline-flex items-center gap-0.5"
                  title="Jump to this moment"
                  data-testid={`video-note-seek-${n.id}`}
                >
                  <Play className="w-2.5 h-2.5" />{fmtTs(n.timestamp_sec)}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-300 whitespace-pre-wrap break-words">{n.note}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{n.author_name}</div>
                </div>
                {(n.author_id === user?.id || user?.role === "org_admin" || user?.role === "super_admin") && (
                  <button
                    onClick={() => remove(n.id)}
                    className="shrink-0 text-zinc-500 hover:text-red-400"
                    title="Delete note"
                    data-testid={`video-note-delete-${n.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1.5" data-testid={`video-note-input-${videoKey}`}>
        <input
          value={tsInput}
          onChange={(e) => setTsInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="0:00"
          className="w-16 h-7 px-1.5 rounded border border-white/10 bg-[#0A0A0A] text-xs font-mono text-zinc-200 focus:border-gold focus:outline-none"
          data-testid={`video-note-time-${videoKey}`}
        />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a note at this moment…"
          className="flex-1 h-7 px-2 rounded border border-white/10 bg-[#0A0A0A] text-xs text-zinc-200 focus:border-gold focus:outline-none"
          data-testid={`video-note-text-${videoKey}`}
        />
        <button
          onClick={add}
          disabled={saving}
          className="h-7 px-2 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          data-testid={`video-note-add-${videoKey}`}
        >
          <MessageSquarePlus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
}
